/**
 * @fileoverview Common formatting and normalization utilities
 */

/**
 * Normalizes gender value to standard format
 * @param {string|number} gender - The gender value to normalize
 * @returns {string} Normalized gender value ('M', 'F', or '')
 */
export const normalizeGender = (gender) => {
  if (!gender) return '';
  const g = String(gender).toUpperCase();
  if (g === 'M' || g === 'MALE' || g === '1') return 'M';
  if (g === 'F' || g === 'FEMALE' || g === '2') return 'F';
  return '';
};

/**
 * Formats a date string for display
 * @param {string|Date} date - The date to format
 * @param {string} locale - The locale for formatting (default: 'pt-BR')
 * @returns {string} Formatted date string or '-' if invalid
 */
export const formatDate = (date, locale = 'pt-BR') => {
  if (!date) return '-';
  try {
    // Parse as local date to avoid timezone offset issues.
    // new Date("YYYY-MM-DD") treats the string as UTC midnight, which shifts
    // the date to the previous day in UTC-3 (Brazil). Passing year/month/day
    // separately creates the date in local time. Month is 0-based in JS
    // (0 = January), so we subtract 1.
    const [year, month, day] = date.toString().split('T')[0].split('-');
    return new Date(year, month - 1, day).toLocaleDateString(locale);
  } catch {
    return '-';
  }
};

/**
 * Formats a date for input fields (YYYY-MM-DD)
 * @param {string|Date} date - The date to format
 * @returns {string} Date in YYYY-MM-DD format or empty string
 */
export const formatDateForInput = (date) => {
  if (!date) return '';
  try {
    const d = new Date(date);
    return d.toISOString().split('T')[0];
  } catch {
    return '';
  }
};

/**
 * Formats a datetime string for display
 * @param {string|Date} datetime - The datetime to format
 * @param {string} locale - The locale for formatting (default: 'pt-BR')
 * @returns {string} Formatted datetime string or '-' if invalid
 */
export const formatDateTime = (datetime, locale = 'pt-BR') => {
  if (!datetime) return '-';
  try {
    return new Date(datetime).toLocaleString(locale, { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return '-';
  }
};

/**
 * Formats a CPF number for display
 * @param {string|number} value - The CPF value to format
 * @returns {string} Formatted CPF (XXX.XXX.XXX-XX) or original value if invalid
 */
export const formatCpf = (value) => {
  if (!value) return '';
  const digits = String(value).replace(/\D/g, '');
  if (digits.length !== 11) return value;
  return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
};

/**
 * Formats a CNPJ number for display
 * @param {string|number} value - The CNPJ value to format
 * @returns {string} Formatted CNPJ (XX.XXX.XXX/XXXX-XX) or original value if invalid
 */
export const formatCnpj = (value) => {
  if (!value) return '';
  const digits = String(value).replace(/\D/g, '');
  if (digits.length !== 14) return value;
  return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
};

/**
 * Formats a CEP (Brazilian postal code) for display
 * @param {string|number} value - The CEP value to format
 * @returns {string} Formatted CEP (XXXXX-XXX) or original value if invalid
 */
export const formatCep = (value) => {
  if (!value) return '';
  const digits = String(value).replace(/\D/g, '');
  if (digits.length !== 8) return value;
  return digits.replace(/(\d{5})(\d{3})/, '$1-$2');
};
